import useSWR from 'swr';
import { userService } from '../services/userService';
import { postService } from '../services/postService';
import { reviewService, Review } from '../services/reviewService';

const SWR_OPTIONS = {
  revalidateOnFocus: false,
  dedupingInterval: 10000,
};

/**
 * Fetch denormalized user stats (total likes, average rating)
 */
export function useUserStats(userId: string | undefined) {
  const { data, isLoading } = useSWR(
    userId ? `user-stats-${userId}` : null,
    async () => {
      const res = await userService.getUserStats(userId!);
      return res.success ? res.data : null;
    },
    SWR_OPTIONS
  );

  return {
    totalLikes: data?.totalLikes || 0,
    averageRating: data?.averageRating || 0,
    reviewCount: data?.reviewCount || 0,
    loading: isLoading,
  };
}

/**
 * Fetch user profile (own or by ID)
 */
export function useUserProfile(userId?: string) {
  const { data, error, isLoading, mutate } = useSWR(
    userId ? `profile-${userId}` : 'profile-me',
    async () => {
      if (userId) {
        const res = await userService.getUserProfileById(userId);
        return res.success ? res.data?.userProfile : null;
      }
      const res = await userService.getUserProfile();
      return res.success ? res.data?.userProfile : null;
    },
    SWR_OPTIONS
  );

  return { profile: data || null, loading: isLoading, error, refresh: mutate };
}

/**
 * Fetch followers + following counts and lists
 */
export function useFollowData(userId: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR(
    userId ? `follows-${userId}` : null,
    async () => {
      const [followersRes, followingRes] = await Promise.all([
        userService.getFollowers(userId!, { suppressAuthPrompt: true }),
        userService.getFollowing(userId!, { suppressAuthPrompt: true })
      ]);
      return {
        followers: followersRes.followers || [],
        following: followingRes.following || [],
        followersCount: followersRes.followers?.length || 0,
        followingCount: followingRes.following?.length || 0,
      };
    },
    SWR_OPTIONS
  );

  return {
    followers: data?.followers || [],
    following: data?.following || [],
    followersCount: data?.followersCount || 0,
    followingCount: data?.followingCount || 0,
    loading: isLoading,
    error,
    refresh: mutate,
  };
}

/**
 * Fetch creator works (posts)
 */
export function useCreatorWorks(userId: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR(
    userId ? `works-${userId}` : null,
    () => postService.getCreatorPosts(userId!, 100),
    SWR_OPTIONS
  );

  return { works: data || [], loading: isLoading, error, refresh: mutate };
}

/**
 * Fetch user reviews
 */
export function useUserReviews(userId: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR(
    userId ? `reviews-${userId}` : null,
    async () => {
      const res = await reviewService.getUserReviews(userId!);
      if (res.success && res.data) {
        return {
          reviews: res.data.reviews as Review[],
          averageRating: res.data.averageRating,
          reviewCount: res.data.reviewCount,
        };
      }
      return { reviews: [] as Review[], averageRating: 0, reviewCount: 0 };
    },
    SWR_OPTIONS
  );

  return {
    reviews: data?.reviews || [],
    averageRating: data?.averageRating || 0,
    reviewCount: data?.reviewCount || 0,
    loading: isLoading,
    error,
    refresh: mutate,
  };
}

/**
 * Fetch liked and favorited works
 */
export function useInteractedWorks(userId: string | undefined) {
  const { data, error, isLoading } = useSWR(
    userId ? `interactions-${userId}` : null,
    async () => {
      const [liked, favorited] = await Promise.all([
        postService.getInteractedPosts(userId!, 'like', 100, { suppressAuthPrompt: true }),
        postService.getInteractedPosts(userId!, 'bookmark', 100, { suppressAuthPrompt: true }),
      ]);
      return { likes: liked, favorites: favorited };
    },
    SWR_OPTIONS
  );

  return {
    likes: data?.likes || [],
    favorites: data?.favorites || [],
    loading: isLoading,
    error,
  };
}
